import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Kolom, Lijst } from '@xxtract/tabelfilters';
import { Button, Card, CardContent, CardHeader, CardTitle, TreeTable, type TreeNode, type TreeTableColumn } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage, isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useServerTabelData, useTabelStand } from '@/components/tabel';
import { usePortfolio } from '../hooks/usePortfolio';
import { useProcessenFilter } from '../hooks/useProcessenFilter';
import { PROCESS_FILTER_TEKSTEN, ProcessFilters, processFilterTestId } from '../components/ProcessFilters';
import { formatFetchedAt } from '../format';
import { PROCESS_FILTER_VELDEN, PROCESSEN_PAGINAFILTER } from '../procesLijsten';
import type { PortfolioRow } from '../processes.api';

/** Scheidingsteken tussen twee vertaalde stukken — interpunctie, geen te vertalen tekst. */
const SEPARATOR = ' · ';

/**
 * spec-tabelfilters story 11: extern gepagineerd (CAP-10). Sorteren op één bronkolom; geen kolomfilters, want
 * klant, periode en status zijn de gedeelde Processen-paginafilter (story 12) en dienst geeft het portaal niet door.
 */
export const PORTFOLIO_LIJST: Lijst<PortfolioRow> = {
  kolommen: [
    { sleutel: 'orderNumber', kopSleutel: 'processes.portfolio.columns.order', type: 'tekst', filter: false, waarde: (r) => r.orderNumber },
    { sleutel: 'klant', kopSleutel: 'processes.portfolio.columns.customer', type: 'overig', waarde: (r) => r.customer },
    { sleutel: 'diensten', kopSleutel: 'processes.portfolio.columns.services', type: 'overig', waarde: () => null },
    { sleutel: 'voortgang', kopSleutel: 'processes.portfolio.columns.progress', type: 'overig', waarde: () => null },
    { sleutel: 'lastEventAt', kopSleutel: 'processes.portfolio.columns.lastActivity', type: 'datum', filter: false, waarde: (r) => r.lastActivity },
    { sleutel: 'doorlooptijd', kopSleutel: 'processes.portfolio.columns.leadTime', type: 'overig', waarde: () => null },
    { sleutel: 'stilstand', kopSleutel: 'processes.portfolio.columns.stagnation', type: 'overig', waarde: () => null },
  ],
  maxSorteerkolommen: 1,
};

const PORTFOLIO_SLEUTEL = 'portfolio';
const PORTFOLIO_PAGINAGROOTTE = 25;
/** Het portaalschema weigert meer; een oude link wordt vóór het verzoek begrensd. */
const PORTFOLIO_MAX_PAGINAGROOTTE = 50;
const STARTLIJSTEN = [{ lijst: PORTFOLIO_LIJST, standSleutel: PORTFOLIO_SLEUTEL }];
/** Een 400 op `sort` is ook een geweigerd filter; ProcessFilters blijft ongemoeid. */
const PORTFOLIO_FILTER_VELDEN = { ...PROCESS_FILTER_VELDEN, sort: 'tabel.sorteerOp' } as const;

/** Kolombreedtes: kop en cellen delen ze (TreeTable), in px zodat het raster vast ligt. De eerste draagt ook de pijlplek. */
const BREEDTE: Record<string, string> = {
  orderNumber: 'w-[140px] shrink-0',
  klant: 'w-[130px] shrink-0',
  diensten: 'w-[120px] shrink-0',
  voortgang: 'w-[180px] shrink-0',
  lastEventAt: 'w-[130px] shrink-0',
  doorlooptijd: 'w-[140px] shrink-0',
  stilstand: 'w-[90px] shrink-0',
};
const CEL = 'truncate text-xs text-[var(--foreground-muted)]';

function StagnationLabel({ item }: { item: PortfolioRow }) {
  const { t } = useTranslation();
  if (item.stagnation.count <= 0) {
    return <span className="text-[var(--foreground-muted)]">{t('common.notAvailable')}</span>;
  }
  return (
    <span className="rounded-full bg-[var(--warning-light)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--warning-text)]" data-testid="portfolio-stagnant-badge">
      {t('processes.knelpunt.stagnantShort')}
    </span>
  );
}

function itemCells(item: PortfolioRow, locale: string, t: TFunction): Record<string, ReactNode> {
  return {
    orderNumber: <span className="truncate text-xs font-medium text-[var(--foreground)]" data-stagnant={item.stagnation.count > 0} data-testid="portfolio-row">{item.orderNumber}</span>,
    klant: <span className={CEL}>{item.customer ?? t('common.notAvailable')}</span>,
    diensten: <span className={CEL}>{item.services.length === 0 ? t('common.notAvailable') : item.services.join(', ')}</span>,
    voortgang: (
      <span className={CEL}>
        {item.progress.length === 0
          ? t('common.notAvailable')
          : item.progress.map((entry) => t('processes.portfolio.progressEntry', { service: entry.serviceCode, x: entry.progress.x, y: entry.progress.y })).join(SEPARATOR)}
      </span>
    ),
    lastEventAt: <span className={CEL}>{item.lastActivity != null ? formatFetchedAt(item.lastActivity, locale) : t('common.notAvailable')}</span>,
    doorlooptijd: (
      <span className={CEL}>
        {item.leadTime.ms != null
          ? t('processes.detail.leadTimeValue', { days: Math.round(item.leadTime.ms / (24 * 60 * 60 * 1000)), basis: t(`processes.detail.leadTimeBasis.${item.leadTime.basis}`) })
          : t('processes.placeholder.notYetAvailable')}
      </span>
    ),
    stilstand: <StagnationLabel item={item} />,
  };
}

export function Portfolio() {
  const { t, i18n } = useTranslation();
  const kopRef = useRef<HTMLHeadingElement>(null);
  const kaartTitelId = useId();
  const { values: filterValues, setFilters, wis, start, geweigerd, defaultStilstandDrempelWerkdagen } = useProcessenFilter(STARTLIJSTEN);
  // Een andere Processen-filter zet de portfolio via `afhankelijkVan` op pagina 1.
  const stand = useTabelStand(PORTFOLIO_LIJST, PORTFOLIO_SLEUTEL, { start: start[PORTFOLIO_SLEUTEL], afhankelijkVan: PROCESSEN_PAGINAFILTER, paginagrootte: PORTFOLIO_PAGINAGROOTTE });
  const context = useMemo(() => ({ t: (s: string) => t(s) }), [t]);
  const paginagrootte = Math.min(stand.paginagrootte ?? PORTFOLIO_PAGINAGROOTTE, PORTFOLIO_MAX_PAGINAGROOTTE);

  // Het totaal komt na de vraag binnen en begrenst een render later de bewaarde pagina; de pager blijft op `hasMore`.
  const [totaal, setTotaal] = useState<number>();
  const tabel = useServerTabelData(PORTFOLIO_LIJST, { ...stand, paginagrootte }, totaal === undefined ? undefined : { totaal }, { extern: true });
  const { page: _page, ...kolommen } = tabel.parameters;
  const portfolioQuery = usePortfolio(filterValues, tabel.pagina, kolommen);
  // Alleen een echt antwoord telt; een onbereikbare of weigerende bron (totaal 0) laat de bewaarde pagina staan.
  const antwoordTotaal = portfolioQuery.data?.data.sources.procesManagement === 'ok' ? portfolioQuery.data.data.total : undefined;
  useEffect(() => {
    if (antwoordTotaal !== undefined) setTotaal(antwoordTotaal);
  }, [antwoordTotaal]);

  // CAP-2: een geweigerd filter keert niet terug vóór de filterbalk — die is de weg terug.
  const filterRejected = isFilterRejected(portfolioQuery.error, PORTFOLIO_FILTER_VELDEN, portfolioQuery.data?.data.sources.procesManagement);
  if (portfolioQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={portfolioQuery.error} onRetry={() => portfolioQuery.refetch()} />;
  }

  const data = filterRejected ? undefined : portfolioQuery.data?.data;
  const standaard = PORTFOLIO_LIJST.standaardSortering?.(context);

  const columns: TreeTableColumn[] = PORTFOLIO_LIJST.kolommen.map((k) => ({
    id: k.sleutel,
    label: t(k.kopSleutel),
    header: <SorteerbareKop kolom={k as Kolom<never, never>} max={1} onKlik={() => stand.klikOpKop(k.sleutel)} sortering={stand.sortering} standaard={standaard} />,
    ariaSort: ariaSort(stand.sortering, k.sleutel),
    className: BREEDTE[k.sleutel]!,
  }));
  const nodes: TreeNode[] = (data?.items ?? []).map((item) => ({
    id: item.orderNumber,
    label: item.orderNumber,
    cells: itemCells(item, i18n.language, t),
  }));

  return (
    <div className="space-y-5" data-testid="portfolio-page">
      <header>
        <h1 ref={kopRef} tabIndex={-1} className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('processes.portfolio.title')}</h1>
        <p className="text-xs text-[var(--foreground-muted)]">{t('processes.portfolio.subtitle')}</p>
        {data ? (
          <p aria-live="polite" className="text-xs text-[var(--foreground-muted)]" data-testid="portfolio-updated-at">
            {t('processes.portfolio.countSubtitle', { count: data.total })}{SEPARATOR}{t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}
          </p>
        ) : null}
      </header>

      <ProcessFilters defaultStilstandDrempelWerkdagen={defaultStilstandDrempelWerkdagen} onChange={setFilters} values={filterValues} />

      <FilterRejectedMessage
        baseTestId={processFilterTestId}
        error={portfolioQuery.error}
        focusNaWissen={kopRef}
        linkGeweigerd={geweigerd}
        onClear={() => {
          wis();
          stand.reset();
        }}
        rejected={filterRejected}
        teksten={PROCESS_FILTER_TEKSTEN}
        velden={PORTFOLIO_FILTER_VELDEN}
      />

      {/* Geweigerd filter: geen kaart, maar de filterbalk (reset) blijft. */}
      {filterRejected ? <Filterbalk context={context} data={tabel} lijst={PORTFOLIO_LIJST} stand={stand} /> : null}

      {portfolioQuery.isLoading ? <div className="h-48 animate-pulse rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--background-muted)]" data-testid="portfolio-loading" /> : null}

      {data ? (
        <>
          {data.sources.procesManagement === 'unavailable' ? (
            <p className="text-xs text-[var(--info-text)]" role="status">{t('processes.degraded.title')}</p>
          ) : null}

          <Card data-testid="portfolio-card">
            <CardHeader>
              <CardTitle id={kaartTitelId}>{t('processes.portfolio.title')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Filterbalk context={context} data={{ ...tabel, totaal: data.total }} lijst={PORTFOLIO_LIJST} stand={stand} />
              {nodes.length === 0 ? (
                <p className="text-xs text-[var(--foreground-muted)]" data-testid="portfolio-empty">{t('processes.portfolio.empty')}</p>
              ) : (
                // Kop en rijen delen één scrollvak binnen de kaart, anders duwt het kopraster op een smal scherm de
                // pagina breed. Focusbaar (naam = kaarttitel), omdat de rijen pijl-links/rechts zelf afvangen.
                // Omlijning met ! (niet ring): de globale *:focus-visible-regel in globals.css wint anders van elke
                // ring-utility, en een omlijning tekent boven de koprij-achtergrond uit.
                <div aria-labelledby={kaartTitelId} className="overflow-x-auto focus-visible:outline-2! focus-visible:-outline-offset-2! focus-visible:outline-[var(--secondary)]!" role="region" tabIndex={0}>
                  <div className="min-w-max">
                    <TreeTable ariaLabel={t('processes.portfolio.title')} className="overflow-x-visible" columns={columns} data-testid="portfolio-tree" nodes={nodes} />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <Button data-testid="portfolio-prev-page" disabled={tabel.pagina <= 1} onClick={() => stand.zetPagina(tabel.pagina - 1)} size="sm" variant="secondary">
                  {t('processes.stepDetail.previousPage')}
                </Button>
                <span className="text-xs text-[var(--foreground-muted)]" data-testid="portfolio-page-indicator">{t('processes.stepDetail.pageIndicator', { page: tabel.pagina })}</span>
                <Button data-testid="portfolio-next-page" disabled={!data.hasMore} onClick={() => stand.zetPagina(tabel.pagina + 1)} size="sm" variant="secondary">
                  {t('processes.stepDetail.nextPage')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
